import { DcEnvironment, DcTenant } from '@rma/generic/core/instance';
import { appendPathToUrl } from '@rma/generic/core/urls';

export type RmaUrlFactory = (path?: string) => string;

export interface RmaUrlOverrideConfig {
  baseUrl: string;
}

export type RmaUrlOverride = string | RmaUrlOverrideConfig;

// There are 4 types of URL factories:
// 1. takes a function that returns a URL based on the tenant and environment
type EnvironmentTenantedApiUrlFn = (tenant: DcTenant, env: DcEnvironment) => string;
// 2. takes a function that returns a URL based on the tenant only
type TenantedApiUrlFn = (tenant: DcTenant) => string;
// 3. takes a function that returns a URL based on the environment only
type EnvironmentApiUrlFn = (env: DcEnvironment) => string;
// 4. takes a function that returns a URL without any tenant or environment context
type GlobalApiUrlFn = () => string;

function resolveOverrideBaseUrl(override: RmaUrlOverride): string {
  return typeof override === 'string' ? override : override.baseUrl;
}

function createOverrideUrlFactory(override: RmaUrlOverride): RmaUrlFactory {
  const baseUrl = resolveOverrideBaseUrl(override);
  return (path?: string): string => {
    return !path ? baseUrl : appendPathToUrl(path, baseUrl);
  };
}

export function environmentTenantedUrlFactory(
  urlFn: EnvironmentTenantedApiUrlFn,
  override?: RmaUrlOverride,
): (tenant: DcTenant, env: DcEnvironment) => RmaUrlFactory {
  return (tenant: DcTenant, env: DcEnvironment): RmaUrlFactory => {
    if (override) {
      return createOverrideUrlFactory(override);
    }
    return (path?: string): string => {
      const baseUrl = urlFn(tenant, env);
      return !path ? baseUrl : appendPathToUrl(path, baseUrl);
    };
  };
}

export function environmentUrlFactory(urlFn: EnvironmentApiUrlFn, override?: RmaUrlOverride): (env: DcEnvironment) => RmaUrlFactory {
  return (env: DcEnvironment): RmaUrlFactory => {
    if (override) {
      return createOverrideUrlFactory(override);
    }
    return (path?: string): string => {
      const baseUrl = urlFn(env);
      return !path ? baseUrl : appendPathToUrl(path, baseUrl);
    };
  };
}

export function tenantedUrlFactory(urlFn: TenantedApiUrlFn, override?: RmaUrlOverride): (tenant: DcTenant) => RmaUrlFactory {
  return (tenant: DcTenant) => {
    if (override) {
      return createOverrideUrlFactory(override);
    }
    return (path?: string): string => {
      const baseUrl = urlFn(tenant);
      return !path ? baseUrl : appendPathToUrl(path, baseUrl);
    };
  };
}

export function globalUrlFactory(urlFn: GlobalApiUrlFn, override?: RmaUrlOverride): RmaUrlFactory {
  if (override) {
    return createOverrideUrlFactory(override);
  }
  return (path?: string): string => {
    const baseUrl = urlFn();

    return !path ? baseUrl : appendPathToUrl(path, baseUrl);
  };
}
